import { create } from 'zustand';
import client from '../api/client.js';
import { evaluarVersion, POSPONER_MS } from './versionApp.js';
import { puedeActualizarse, infoApp, descargarApk, instalarApk } from '../native/actualizador.js';

// Actualizar la app sin pasar por la Play Store.
//
// El APK publicado vive en la nube (se sube y publica desde el administrador
// de la plataforma). La app pregunta al abrir, al volver a primer plano y cada
// pocas horas; si hay una versión más nueva, ofrece actualizar con un botón.
// Descargar e instalar lo hace el plugin nativo; Android siempre pide
// confirmar la instalación con su propia ventana.

const LLAVE_POSPUESTA = 'gc-pos-actualizacion-pospuesta';
const CADA_MS = 30 * 60 * 1000;   // no preguntar más seguido que esto

const leerPospuesta = () => {
  try { return JSON.parse(localStorage.getItem(LLAVE_POSPUESTA)); } catch { return null; }
};

export const useActualizacionStore = create((set, get) => ({
  instalada:   null,     // { versionCode, versionName } de este APK
  ultima:      null,     // la publicada, si es más nueva que la instalada
  obligatoria: false,
  abierta:     false,    // la hoja de "hay una versión nueva" se ve
  estado:      'nada',   // nada | descargando | instalando | permiso | error
  progreso:    0,        // 0..1
  error:       null,
  revisadaEn:  0,

  /**
   * Pregunta al servidor. `forzar`: desde "Este aparato" — ignora el
   * "Después" y el intervalo. Devuelve 'hay' | 'al-dia' | 'sin-red' | null
   * (no aplica en este aparato).
   */
  async revisar({ forzar = false } = {}) {
    if (!puedeActualizarse()) return null;
    if (get().estado === 'descargando') return 'hay';
    if (!forzar && Date.now() - get().revisadaEn < CADA_MS) return get().ultima ? 'hay' : 'al-dia';
    set({ revisadaEn: Date.now() });

    try {
      const instalada = get().instalada ?? await infoApp();
      // `nube`: directo a la nube aunque haya caja principal, que no sabe de versiones
      const r = await client.get('/app/android', { nube: true, timeout: 10_000 });
      const manifiesto = r.data?.data;
      const ev = evaluarVersion({
        instalada: Number(instalada.versionCode),
        manifiesto,
        pospuesta: forzar ? null : leerPospuesta(),
      });
      set({
        instalada,
        ultima:      ev.hay ? manifiesto.ultima : null,
        obligatoria: ev.obligatoria,
        abierta:     get().abierta || ev.mostrar,
      });
      return ev.hay ? 'hay' : 'al-dia';
    } catch {
      // Sin internet o el servidor no contesta: se vuelve a intentar después
      set({ revisadaEn: 0 });
      return 'sin-red';
    }
  },

  abrir:  () => set({ abierta: true }),
  ocultar: () => set({ abierta: false }),   // la descarga, si va, sigue

  posponer() {
    const { ultima, obligatoria } = get();
    if (obligatoria) return;
    if (ultima) {
      try {
        localStorage.setItem(LLAVE_POSPUESTA, JSON.stringify({ versionCode: ultima.versionCode, hasta: Date.now() + POSPONER_MS }));
      } catch { /* sin almacenamiento: vuelve a avisar al abrir */ }
    }
    set({ abierta: false, estado: get().estado === 'descargando' ? 'descargando' : 'nada', error: null });
  },

  async actualizar() {
    const { ultima, estado } = get();
    if (!ultima || estado === 'descargando') return;
    set({ estado: 'descargando', progreso: 0, error: null });
    try {
      // Si ya estaba descargada y completa, el plugin no la baja otra vez
      await descargarApk(ultima, (progreso) => set({ progreso }));
      set({ estado: 'instalando' });
      await instalarApk(ultima.versionCode);
    } catch (err) {
      if (err?.code === 'PERMISO') set({ estado: 'permiso', abierta: true });
      else set({ estado: 'error', abierta: true, error: err?.message || 'No se pudo actualizar. Vuelve a intentarlo.' });
    }
  },
}));
